import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useConfirm } from 'components/ConfirmDialog';
import { AQISensor } from 'features/aqiSensors/aqiSensors.types';
import {
  SaveAlertData,
  useWeatherAlert,
} from 'features/weatherAlerts/hooks/useWeatherAlert';
import { WeatherAlert } from 'features/weatherAlerts/weatherAlerts.types';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { switchFormRowLabelSx } from 'theme/switchFormRowLabelSx';

import { AqiDescriptions } from './AqiDescriptions';
import {
  AQI_OPTIONS,
  getMenuOptions,
  getSelectedMenuOption,
  getValidationSchema,
} from './utils';

type AqiSensorAlertFormProps = {
  aqiSensor: AQISensor;
  alert?: WeatherAlert;
  fromRoute: string;
};

type AqiSelection = (typeof AQI_OPTIONS)[number];

type FormValues = {
  additionalAlerts?: boolean;
  aqiSelection?: AqiSelection;
  isEnabled?: boolean;
  thresholdValue?: number | string;
};

const getAqiSelection = (alert: WeatherAlert | undefined): AqiSelection => {
  if (!alert) return '';

  if (alert.metadata?.isCustomValue) return 'custom';
  const value = String(alert.criteria[0].thresholdValue ?? '');
  return AQI_OPTIONS.includes(value as AqiSelection)
    ? (value as AqiSelection)
    : '';
};

const getThresholdValue = (
  aqiSelection: string | undefined,
  val: string | number | undefined,
): number => Number(aqiSelection === 'custom' ? val : aqiSelection);

export const AqiSensorAlertForm = (
  props: AqiSensorAlertFormProps,
): JSX.Element => {
  const { aqiSensor, alert, fromRoute } = props;
  const { t } = useTranslation();
  const { saveAlert, deleteAlert } = useWeatherAlert({ alertId: alert?.id });
  const confirm = useConfirm();

  const { control, formState, handleSubmit } = useForm<FormValues>({
    resolver: yupResolver(getValidationSchema(t)),
    values: {
      additionalAlerts: alert?.metadata?.autoEscalateAqi ?? false,
      aqiSelection: getAqiSelection(alert),
      isEnabled: alert?.isEnabled ?? true,
      thresholdValue: alert?.criteria[0].thresholdValue
        ? Number(alert.criteria[0].thresholdValue)
        : '',
    },
  });

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  const navigateBack = (): void => unblockedNavigate(fromRoute);

  // Needed so we can decide if additional entry field is needed
  const selectedAqi = useWatch({ control, name: 'aqiSelection' });
  const isCustomSelected = selectedAqi === 'custom';

  const onDelete = async (): Promise<void> => {
    if (!alert) return;

    const resp = await confirm(t('aqiSensors.alerts.deleteConfirmation'), {
      title: t('aqiSensors.alerts.deleteNotification'),
    });
    if (!resp) return;

    try {
      await deleteAlert();
      navigateBack();
    } catch (error) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    }
  };

  const onSave: SubmitHandler<FormValues> = async (values): Promise<void> => {
    const thresholdValue = getThresholdValue(
      values.aqiSelection,
      values.thresholdValue,
    );
    const data: SaveAlertData = {
      id: alert?.id,
      ...(alert?.id ? { isEnabled: values.isEnabled } : {}), // Only set isEnabled when editing
      stationId: aqiSensor.id,
      criteria: [
        {
          fieldName: 'aqi',
          operator: 'gte',
          thresholdValue,
        },
      ],
      metadata: {
        autoEscalateAqi: values.additionalAlerts,
        isCustomValue: isCustomSelected,
      },
    };

    try {
      await saveAlert(data);
      navigateBack();
    } catch (finalError) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error: finalError });
    }
  };

  return (
    <Stack spacing={2}>
      <AqiDescriptions />
      <Typography variant="h2">{aqiSensor?.name}</Typography>

      <Typography>
        {t('aqiSensors.alerts.inputs.aqiSelection.header')}
      </Typography>
      <Controller
        control={control}
        name="aqiSelection"
        render={({ field, fieldState }) => {
          const { ref, value, ...muiFieldProps } = field;
          return (
            <FormControl required>
              <InputLabel id="aqiSelection-input-label" required>
                {t('aqiSensors.alerts.inputs.aqiSelection.label')}
              </InputLabel>
              <Select
                label={t('aqiSensors.alerts.inputs.aqiSelection.label')}
                labelId="aqiSelection-input-label"
                error={Boolean(fieldState.error)}
                fullWidth
                renderValue={(v) => getSelectedMenuOption(v, t)}
                value={value}
                {...muiFieldProps}
              >
                {getMenuOptions(t).map((opt) => opt)}
              </Select>
              {(fieldState.error?.message || !isCustomSelected) && (
                <FormHelperText error={Boolean(fieldState.error?.message)}>
                  {fieldState.error?.message ??
                    t('aqiSensors.alerts.inputs.thresholdValue.helperText')}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />

      {isCustomSelected && (
        <Controller
          control={control}
          name="thresholdValue"
          render={({ field, fieldState }) => {
            const { ref, value, ...muiFieldProps } = field;
            return (
              <FormControl required>
                <TextField
                  label={t('aqiSensors.alerts.inputs.thresholdValue.label')}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ||
                    t('aqiSensors.alerts.inputs.thresholdValue.helperText')
                  }
                  fullWidth
                  required
                  slotProps={{
                    htmlInput: {
                      inputMode: 'decimal',
                      maxLength: 5,
                    },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {t('aqiSensors.alerts.inputs.thresholdValue.units')}
                        </InputAdornment>
                      ),
                    },
                  }}
                  value={value}
                  {...muiFieldProps}
                />
              </FormControl>
            );
          }}
        />
      )}

      <Controller
        control={control}
        name="additionalAlerts"
        render={({ field }) => {
          const { ref, value, ...muiFieldProps } = field;
          return (
            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Switch checked={value} inputRef={ref} {...muiFieldProps} />
                }
                label={t('aqiSensors.alerts.inputs.additionalAlerts.label')}
                labelPlacement="start"
                sx={switchFormRowLabelSx}
              />
            </FormControl>
          );
        }}
      />

      {alert && (
        <Controller
          control={control}
          name="isEnabled"
          render={({ field }) => {
            const { ref, value, ...muiFieldProps } = field;
            return (
              <FormControl fullWidth>
                <FormControlLabel
                  control={<Switch checked={value} {...muiFieldProps} />}
                  label={t('aqiSensors.alerts.inputs.isEnabled.label')}
                  labelPlacement="start"
                  sx={switchFormRowLabelSx}
                />
              </FormControl>
            );
          }}
        />
      )}

      {alert && (
        <Button
          fullWidth
          size="large"
          disabled={formState.isSubmitting}
          onClick={onDelete}
          color="error"
        >
          {t('aqiSensors.alerts.deleteNotification')}
        </Button>
      )}

      <Button
        fullWidth
        size="large"
        disabled={formState.isSubmitting}
        onClick={handleSubmit(onSave)}
      >
        {alert
          ? t('aqiSensors.alerts.editNotification')
          : t('aqiSensors.alerts.addNotification')}
      </Button>
    </Stack>
  );
};
